import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiLoader, FiX, FiInfo, FiZap, FiDownload, FiType, FiGlobe, FiFileText, FiClock, FiUpload, FiFilm } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { getClientId } from "./audioShared";
import { card, labelClass, Pills, Slider, saveBlob, slug } from "./VideoStudioPanel";
import VideoDropZone from "./videofx/VideoDropZone";
import ResultsList from "./videofx/ResultsList";
import useVideoFx from "./videofx/useVideoFx";
import SubtitlePreview from "./subs/SubtitlePreview";
import CueEditor from "./subs/CueEditor";
import { upscaleVideo } from "../video/upscale/process";
import { clockText, problemWith, sizeForShortSide, sizeText, estimateBytes } from "../video/upscale/plan";
import { recordableFormats } from "../video/timeline";
import { buildCues, cueAt, finalize, parseSubtitles, shiftAll, toSrt, toTxt, toVtt } from "../video/subs/cues";
import { extractAudio, MAX_MINUTES } from "../video/subs/audio";
import { CaptionEngine, DEFAULT_STYLE, FONTS, POSITIONS, PRESETS, PRESET_IDS } from "../video/subs/render";
import { SLOW_LANGUAGES, SPEECH_LANGUAGES, isRtlLanguage } from "../video/subs/languages";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const WIDTHS = [["32", "Narrow"], ["42", "Standard"], ["52", "Wide"]];
const saveKey = (file, seconds) => `subs:v1:${file.name}:${file.size}:${Math.round(seconds)}`;

// AI Auto Subtitles and Captioning: speech recognition writes the subtitles of a video, they are timed to the pauses of the real sound, can be edited and translated,
// and are saved as SRT / WebVTT files or burned into the video. The video itself never leaves the computer; only its sound goes to the speech recogniser.
export default function AutoSubtitlesTool() {
  const fx = useVideoFx();
  const { file, opened, fps, busy } = fx;
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [cues, setCuesState] = useState([]);
  const [language, setLanguage] = useState("auto");
  const [spoken, setSpoken] = useState("");                    // the language the recogniser says was spoken
  const [maxChars, setMaxChars] = useState(42);
  const [maxLines, setMaxLines] = useState(2);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [which, setWhich] = useState("text");
  const [working, setWorking] = useState(null);                // { text } while the subtitles are being made
  const [problem, setProblem] = useState("");
  const [notice, setNotice] = useState("");
  const [engine, setEngine] = useState("");
  const [languages, setLanguages] = useState([]);
  const [target, setTarget] = useState("en");
  const [translatedTo, setTranslatedTo] = useState("");
  const [translating, setTranslating] = useState("");
  const [now, setNow] = useState(0);
  const [outShort, setOutShort] = useState(0);                 // 0 = the size of the video
  const previewRef = useRef(null);
  const subtitleInput = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => { apiRequest("/core/translation/languages/").then(({ ok, data }) => { if (aliveRef.current && ok) setLanguages(data.languages || []); }); }, []);

  const setCues = useCallback((next) => setCuesState([...next].sort((a, b) => a.start - b.start)), []);
  const limits = useMemo(() => ({ maxChars, maxLines }), [maxChars, maxLines]);
  const look = useMemo(() => ({ cues, style, which: translatedTo ? which : "text", maxChars, maxLines }), [cues, style, which, translatedTo, maxChars, maxLines]);
  const active = cueAt(cues, now);
  const rtl = isRtlLanguage(spoken || (language === "auto" ? "" : language));

  // the subtitles of a video are kept in this browser, so that a reload does not lose the editing
  useEffect(() => {
    if (!opened || !file) return undefined;
    const id = setTimeout(() => {
      try {
        if (cues.length) localStorage.setItem(saveKey(file, opened.seconds), JSON.stringify({ cues, spoken, translatedTo, style, which }));
      } catch { /* storage is full or off: the subtitles are simply not kept */ }
    }, 600);
    return () => clearTimeout(id);
  }, [cues, spoken, translatedTo, style, which, file, opened]);

  const onOpened = useCallback((next, chosen) => {
    try {
      const saved = JSON.parse(localStorage.getItem(saveKey(chosen, next.seconds)) || "null");
      if (saved && Array.isArray(saved.cues) && saved.cues.length) {
        setCuesState(saved.cues); setSpoken(saved.spoken || ""); setTranslatedTo(saved.translatedTo || ""); setStyle({ ...DEFAULT_STYLE, ...(saved.style || {}) }); setWhich(saved.which || "text");
        setNotice("The subtitles you made for this video earlier were put back.");
      }
    } catch { /* nothing saved, or unreadable */ }
  }, []);

  const take = (chosen) => {
    setCuesState([]); setSpoken(""); setTranslatedTo(""); setProblem(""); setNotice(""); setEngine(""); setNow(0);
    fx.take(chosen, (next) => onOpened(next, chosen));
  };
  const startOver = () => {
    try { if (file && opened) localStorage.removeItem(saveKey(file, opened.seconds)); } catch { /* nothing to remove */ }
    setCuesState([]); setSpoken(""); setTranslatedTo(""); setNotice(""); setWhich("text");
  };

  // ---------------------------------------------------------------------------------------- making the subtitles
  const create = async () => {
    setProblem("");
    setNotice("");
    previewRef.current?.seek(0);
    setWorking({ text: "Reading the video..." });
    let jobId = null;
    const clientId = getClientId();
    try {
      if (opened.seconds > MAX_MINUTES * 60) throw new Error(`This video is ${Math.round(opened.seconds / 60)} minutes long. Subtitles can be made for up to ${MAX_MINUTES} minutes at a time - cut it into shorter parts.`);
      const { wav, activity } = await extractAudio(file, (text) => aliveRef.current && setWorking({ text }));
      if (!aliveRef.current) return;
      setWorking({ text: "Sending the sound to the speech recogniser..." });
      const form = new FormData();
      form.append("client_id", clientId);
      form.append("kind", "transcription");
      form.append("mode", "subtitles");
      form.append("language", language);
      form.append("original", wav, "sound.wav");
      const created = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
      if (!created.ok) throw new Error(created.data?.original || errorMessage(created.data));
      let job = created.data;
      jobId = job.id;
      for (let n = 0; n < 600 && aliveRef.current && (job.status === "queued" || job.status === "processing"); n += 1) {
        const [done, total] = String(job.progress || "").split("/");
        setWorking({ text: total ? `Recognising the speech: part ${Math.min(Number(done) + 1, Number(total))} of ${total}...` : "Recognising the speech..." });
        await sleep(2500);
        const polled = await apiRequest(`/core/voice-clone/${job.id}/?client_id=${encodeURIComponent(clientId)}`);
        if (polled.ok) job = polled.data;
      }
      if (!aliveRef.current) return;
      if (job.status !== "completed" || !Array.isArray(job.segments) || !job.segments.length) throw new Error(job.error_message || "The speech could not be recognised right now. Please try again in a minute.");
      setWorking({ text: "Timing the subtitles..." });
      const made = buildCues(job.segments, activity, limits);
      if (!made.length) throw new Error("No speech was found in this video.");
      setCuesState(made);
      setSpoken(job.detected_language && job.detected_language !== "auto" ? job.detected_language : language === "auto" ? "" : language);
      setEngine(job.engine || "");
      setTranslatedTo("");
      setWhich("text");
    } catch (e) {
      if (aliveRef.current) setProblem(e.message || "The subtitles could not be made. Please try again.");
    }
    if (jobId) apiRequest(`/core/voice-clone/${jobId}/?client_id=${encodeURIComponent(clientId)}`, { method: "DELETE" });      // the transcript is not kept on the server
    if (aliveRef.current) setWorking(null);
  };

  const loadSubtitleFile = async (chosen) => {
    if (!chosen) return;
    setProblem("");
    try {
      const text = await chosen.text();
      let made = parseSubtitles(text);
      if (!made.length) throw new Error("That file has no subtitles in it. Use an .srt or .vtt file.");
      if (opened && made[made.length - 1].start > opened.seconds + 5) setNotice("The subtitles run longer than the video - check that they belong to it.");
      made = finalize(made, limits);
      setCuesState(made); setTranslatedTo(""); setWhich("text"); setEngine("");
    } catch (e) {
      setProblem(e.message || "That file could not be read.");
    }
  };

  // ---------------------------------------------------------------------------------------- translating
  const translate = async () => {
    setProblem("");
    setTranslating("Translating...");
    try {
      const texts = cues.map((c) => c.text);
      const out = [];
      for (let i = 0; i < texts.length; i += 40) {
        setTranslating(`Translating... ${Math.min(i + 40, texts.length)} of ${texts.length}`);
        const { ok, data } = await apiRequest("/core/transcribe/translate/", { method: "POST", body: { paragraphs: texts.slice(i, i + 40), target } });
        if (!ok) throw new Error(data?.detail || data?.paragraphs || errorMessage(data));
        out.push(...data.paragraphs);
      }
      if (!aliveRef.current) return;
      setCuesState(cues.map((c, i) => ({ ...c, alt: out[i] || "" })));
      setTranslatedTo(target);
      setWhich("alt");
    } catch (e) {
      if (aliveRef.current) setProblem(e.message || "The translation did not work. Please try again.");
    }
    if (aliveRef.current) setTranslating("");
  };
  const targetName = (code) => languages.find((l) => l.key === code)?.label || code;

  // ---------------------------------------------------------------------------------------- saving
  const base = file ? slug(file.name.replace(/\.[^.]+$/, "")) : "subtitles";
  const suffix = translatedTo && look.which !== "text" ? `-${translatedTo}` : "";
  const download = (kind) => {
    const w = look.which;
    const text = kind === "srt" ? toSrt(cues, w, limits) : kind === "vtt" ? toVtt(cues, w, limits) : toTxt(cues, w);
    saveBlob(`${base}${suffix}.${kind}`, new Blob([kind === "vtt" ? text : `﻿${text}`], { type: "text/plain;charset=utf-8" }));
  };

  const shorts = opened ? [0, 720, 1080].filter((s) => s === 0 || s < Math.min(opened.w, opened.h) * 0.95) : [0];
  const outSize = opened ? (outShort ? sizeForShortSide(opened.w, opened.h, outShort) : [opened.w % 2 ? opened.w - 1 : opened.w, opened.h % 2 ? opened.h - 1 : opened.h]) : [0, 0];
  const burnProblem = opened ? problemWith({ w: outSize[0], h: outSize[1], seconds: opened.seconds, fps: fps || 30, bytes: file?.size || 0 }) : "";
  const burn = () => {
    if (!opened || burnProblem || !formats.length || !cues.length) return;
    previewRef.current?.seek(0);
    fx.run(async ({ signal, onProgress, onCanvas }) => ({
      result: await upscaleVideo({ file, size: outSize, look, makeEngine: (canvas) => new CaptionEngine(canvas), bitrateScale: 1.2, mime: formats[format].mime, fps: fps || 30, onProgress, onCanvas, signal }),
      ext: formats[format].ext, look: "With subtitles", extra: `${cues.length} subtitles${look.which === "alt" ? ` in ${targetName(translatedTo)}` : look.which === "both" ? " in two languages" : ""}`,
    }));
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Auto Subtitles &amp; Captioning</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Add subtitles to any video in minutes. AI speech recognition writes what is said, the lines are timed to the real pauses in the sound, and you can fix any word, translate them into other languages,
        choose how they look, and save an SRT or WebVTT file - or a new video with the subtitles burned in. Your video stays on your computer; only its sound is sent to be recognised.
      </p>

      {!opened ? <VideoDropZone reading={fx.reading} onFile={take} hint="MP4, MOV or WebM with sound, up to 600 MB and 20 minutes." /> : (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 items-start">
            <div className={card}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-sm text-gray-200 truncate" title={file.name}>{file.name} <span className="text-gray-500">· {opened.w}×{opened.h} · {clockText(opened.seconds)} · {sizeText(file.size)}</span></span>
                <button type="button" onClick={fx.clear} disabled={!!busy || !!working} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 shrink-0 disabled:opacity-40"><FiX size={13} /> Another video</button>
              </div>
              {busy ? (
                <div>
                  <div ref={fx.liveHost} role="img" aria-label="The video being saved with subtitles" className="rounded-xl bg-black min-h-[200px]" />
                  <p className="text-xs text-gray-500 mt-2 text-center">This is the video being saved, frame by frame.</p>
                </div>
              ) : <SubtitlePreview key={opened.url} ref={previewRef} opened={opened} look={look} onTime={setNow} />}
              {active && !busy && <p className="text-xs text-gray-500 mt-2 text-center">Showing subtitle {cues.indexOf(active) + 1} of {cues.length}</p>}
            </div>

            <div className="space-y-4 lg:sticky lg:top-24">
              {!cues.length ? (
                <div className={`${card} space-y-6`}>
                  <div>
                    <label htmlFor="sub-lang" className={labelClass}>1. Language spoken in the video</label>
                    <select id="sub-lang" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!!working} className="w-full px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light">
                      {SPEECH_LANGUAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    {SLOW_LANGUAGES.has(language) && <p className="text-xs text-gray-400 mt-2">{language === "auto" ? "Automatic detection" : language === "ur" ? "Urdu" : "Punjabi"} uses the Whisper AI model, whose free servers are sometimes busy. If so, wait a few minutes and try again.</p>}
                  </div>
                  <div>
                    <span className={labelClass}>2. How long a line can be</span>
                    <Pills label="Line length" value={String(maxChars)} onChange={(v) => setMaxChars(Number(v))} options={WIDTHS} />
                    <div className="mt-3"><Pills label="Lines" value={String(maxLines)} onChange={(v) => setMaxLines(Number(v))} options={[["1", "One line"], ["2", "Up to two lines"]]} /></div>
                  </div>
                  {problem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3" role="alert">{problem}</p>}
                  <button type="button" onClick={create} disabled={!!working}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-60">
                    {working ? <><FiLoader className="animate-spin" /> {working.text}</> : <><FiZap /> Create the subtitles</>}
                  </button>
                  <div className="text-xs text-gray-500 space-y-1.5">
                    <p>It takes a minute or two for a short video. The sound is sent to the speech recogniser and deleted afterwards.</p>
                    <p>Already have a subtitle file? <button type="button" onClick={() => subtitleInput.current?.click()} className="text-accent-light hover:underline inline-flex items-center gap-1"><FiUpload size={11} /> Use an .srt or .vtt file</button></p>
                  </div>
                </div>
              ) : (
                <>
                  {notice && <p className="text-sm text-gray-200 bg-white/5 border border-navy-border rounded-xl px-4 py-3 flex items-start gap-2"><FiInfo className="mt-0.5 shrink-0 text-accent-light" /> {notice}</p>}
                  <div className={`${card} space-y-5`}>
                    <span className={labelClass}><FiType className="inline -mt-0.5" size={12} /> How they look</span>
                    <Pills label="Style" value={style.preset} onChange={(preset) => setStyle((s) => ({ ...s, preset }))} options={PRESET_IDS.map((k) => [k, PRESETS[k].label])} />
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <div><span className="text-xs text-gray-400 block mb-1.5">Position</span><Pills label="Position" value={style.position} onChange={(position) => setStyle((s) => ({ ...s, position }))} options={Object.entries(POSITIONS)} /></div>
                      <Slider label="Size" value={style.size} min={60} max={170} step={5} suffix="%" onChange={(size) => setStyle((s) => ({ ...s, size }))} />
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <Pills label="Font" value={style.font} onChange={(font) => setStyle((s) => ({ ...s, font }))} options={Object.entries(FONTS).map(([k, f]) => [k, f.label])} />
                      <label className="flex items-center gap-2 text-xs text-gray-300"><input type="color" value={style.color || "#ffffff"} onChange={(e) => setStyle((s) => ({ ...s, color: e.target.value }))} aria-label="Text colour" className="w-8 h-8 rounded border border-navy-border bg-transparent p-0.5" /> Text colour
                        {style.color && <button type="button" onClick={() => setStyle((s) => ({ ...s, color: "" }))} className="text-gray-500 hover:text-white underline">reset</button>}</label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><span className="text-xs text-gray-400 block mb-1.5">Line length</span><Pills label="Line length" value={String(maxChars)} onChange={(v) => setMaxChars(Number(v))} options={WIDTHS} /></div>
                      <div><span className="text-xs text-gray-400 block mb-1.5">Lines</span><Pills label="Lines" value={String(maxLines)} onChange={(v) => setMaxLines(Number(v))} options={[["1", "One"], ["2", "Two"]]} /></div>
                    </div>
                  </div>

                  <div className={`${card} space-y-3`}>
                    <span className={labelClass}><FiGlobe className="inline -mt-0.5" size={12} /> Translate</span>
                    <div className="flex gap-2">
                      <select aria-label="Translate into" value={target} onChange={(e) => setTarget(e.target.value)} className="flex-1 px-3 py-2.5 rounded-xl bg-navy border border-navy-border text-white text-sm">
                        {languages.length ? languages.map((l) => <option key={l.key} value={l.key}>{l.label}</option>) : <option value="en">English</option>}
                      </select>
                      <button type="button" onClick={translate} disabled={!!translating} className="px-4 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                        {translating ? <><FiLoader className="animate-spin" /> {translating}</> : translatedTo ? "Translate again" : "Translate"}
                      </button>
                    </div>
                    {translatedTo && <><Pills label="Which language to show" value={which} onChange={setWhich} options={[["text", "Original"], ["alt", targetName(translatedTo)], ["both", "Both"]]} />
                      <p className="text-xs text-gray-500">Automatic translation can slip on names and idioms: read it in the list below.</p></>}
                    {problem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3" role="alert">{problem}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

          {cues.length > 0 && (
            <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 items-start">
              <div className={card}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <span className={labelClass.replace("mb-2.5", "") + " !mb-0"}><FiFileText className="inline -mt-0.5" size={12} /> Edit the subtitles <span className="normal-case tracking-normal text-gray-500 font-normal">· {cues.length} in all{engine ? ` · recognised by ${engine}` : ""}</span></span>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400" role="group" aria-label="Move all subtitles">
                    <FiClock size={12} /> Move all:
                    {[[-0.5, "-0.5 s"], [-0.1, "-0.1"], [0.1, "+0.1"], [0.5, "+0.5 s"]].map(([d, l]) => (
                      <button key={l} type="button" onClick={() => setCues(shiftAll(cues, d))} className="px-2 py-1 rounded-full border border-white/20 hover:border-accent-light">{l}</button>
                    ))}
                  </div>
                </div>
                <CueEditor cues={cues} onChange={setCues} activeId={active?.id} translated={!!translatedTo} limits={limits} rtl={rtl} nowTime={now}
                  onPlay={(c) => previewRef.current?.seek(Math.max(0, c.start - 0.05), true)} />
              </div>

              <div className="space-y-4 lg:sticky lg:top-24">
                <div className={`${card} space-y-4`}>
                  <span className={labelClass}><FiDownload className="inline -mt-0.5" size={12} /> Save the subtitles</span>
                  <div className="flex flex-wrap gap-2.5">
                    {[["srt", "SRT file"], ["vtt", "WebVTT file"], ["txt", "Text only"]].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => download(k)} className="px-4 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold inline-flex items-center gap-2"><FiDownload size={14} /> {l}</button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">SRT works on YouTube, Facebook, LinkedIn, VLC and most editors. WebVTT is for websites and HTML5 players.</p>
                </div>

                <div className={`${card} space-y-4`}>
                  <span className={labelClass}><FiFilm className="inline -mt-0.5" size={12} /> Or burn them into the video</span>
                  {shorts.length > 1 && <Pills label="Video size" value={String(outShort)} onChange={(v) => setOutShort(Number(v))} options={shorts.map((s) => [String(s), s ? `${s}p` : "Same size"])} />}
                  {formats.length > 1 && <Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} />}
                  {burnProblem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3" role="alert">{burnProblem}</p>}
                  {busy ? (
                    <div>
                      <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand-gradient transition-all" style={{ width: `${Math.round(busy.progress * 100)}%` }} /></div>
                      <p className="text-sm text-gray-200 mt-3 flex items-center gap-2"><FiLoader className="animate-spin text-accent-light" /> Saving... {Math.round(busy.progress * 100)}% · {clockText(busy.progress * opened.seconds)} of {clockText(opened.seconds)}</p>
                      <p className="text-xs text-gray-500 mt-1.5">Keep this tab open and visible: browsers can only record a video while it plays.</p>
                      <button type="button" onClick={() => { fx.cancelRef.current.cancelled = true; }} className="mt-3 px-5 py-2.5 rounded-full border border-white/20 hover:border-red-400 text-sm font-semibold">Stop</button>
                    </div>
                  ) : (
                    <>
                      <button type="button" onClick={burn} disabled={!formats.length || !!burnProblem}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold disabled:opacity-50">
                        <FiZap /> Save the video with subtitles
                      </button>
                      <p className="text-xs text-gray-500">It takes as long as the video ({clockText(opened.seconds)}), and the new file will be about {sizeText(estimateBytes(outSize[0], outSize[1], fps || 30, opened.seconds) * 1.2)}. Your sound is kept.</p>
                    </>
                  )}
                </div>
                <button type="button" onClick={startOver} disabled={!!busy} className="text-xs text-gray-500 hover:text-red-400 underline">Start over with new subtitles</button>
              </div>
            </div>
          )}
        </div>
      )}
      <input ref={subtitleInput} type="file" accept=".srt,.vtt,.txt,text/plain,text/vtt" className="hidden" onChange={(e) => { loadSubtitleFile(e.target.files?.[0]); e.target.value = ""; }} />
      {fx.error && <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6 flex items-start gap-3" role="alert"><FiInfo className="mt-0.5 shrink-0" /> {fx.error}</p>}
      {!cues.length && problem && !opened && <p className="text-sm text-amber-200 mt-4" role="alert">{problem}</p>}

      <ResultsList results={fx.results} onRemove={fx.removeResult} suffix={() => "subtitled"} />

      <div className="mt-10 grid md:grid-cols-3 gap-4 text-sm text-gray-400">
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">How it works</p>The sound is taken out of your video in your browser and sent to a free AI speech recogniser. The text comes back in pieces of about 12 seconds and is divided into subtitles that begin and end where the speaker does.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Check before you publish</p>AI makes mistakes with names, accents, music and background noise. Play the video and fix any word in the list. Timing is usually within a fraction of a second; the arrows move all subtitles together.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Private</p>Your video is never uploaded - only its sound, which is deleted once the text is back. Your subtitles are kept in this browser so that a reload does not lose them. Videos can be up to {MAX_MINUTES} minutes.</div>
      </div>
    </div>
  );
}
